import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import styles from './AwardIngestionView.module.css';
import {
  awardIngestionErrorMessage,
  useAssignAwardIngestionJob,
  useAwardIngestionStatus,
  useAwardJobDetail,
  useEnqueueAwardRecrawl,
} from '../../hooks/useAwardIngestionStatus';
import type {
  AwardIngestionJobRow,
  AwardIngestionStatusResponse,
} from '../../types/rpc-contracts';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeAgo = (isoValue: string | null): string => {
  if (!isoValue) return '—';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return '< 1 minute ago';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

const toTitle = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());

const statusClassName = (status: string): string => {
  switch (status) {
    case 'running':
      return styles.statusRunning;
    case 'succeeded':
      return styles.statusSucceeded;
    case 'failed':
      return styles.statusFailed;
    case 'needs_review':
      return styles.statusNeedsReview;
    case 'blocked':
      return styles.statusBlocked;
    case 'pending':
    default:
      return styles.statusPending;
  }
};

const summaryDefaults = {
  crawl_pending: 0,
  crawl_inflight: 0,
  extraction_inflight: 0,
  extraction_blocked: 0,
  validation_needs_review: 0,
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return dateFormatter.format(date);
};

const SummaryCards: React.FC<{ summary: AwardIngestionStatusResponse['summary'] }> = ({ summary }) => {
  const cards = useMemo(() => ([
    {
      label: 'Crawl Pending',
      value: summary.crawl_pending,
      hint: 'Awaiting initial crawl',
    },
    {
      label: 'Crawl In Flight',
      value: summary.crawl_inflight,
      hint: 'Active Firecrawl runs',
    },
    {
      label: 'Extraction In Flight',
      value: summary.extraction_inflight,
      hint: 'LLM extraction currently running',
    },
    {
      label: 'Extraction Blocked',
      value: summary.extraction_blocked,
      hint: 'Requires manual intervention',
    },
    {
      label: 'Needs Review',
      value: summary.validation_needs_review,
      hint: 'Waiting for verification',
    },
  ]), [summary]);

  return (
    <div className={styles.summaryGrid}>
      {cards.map((card) => (
        <div key={card.label} className={styles.summaryCard}>
          <span className={styles.summaryLabel}>{card.label}</span>
          <span className={styles.summaryValue}>{card.value}</span>
          <span className={styles.summaryHint}>{card.hint}</span>
        </div>
      ))}
    </div>
  );
};

interface JobTableProps {
  jobs: AwardIngestionJobRow[];
  onSelect: (jobId: string) => void;
}

const JobTable: React.FC<JobTableProps> = ({ jobs, onSelect }) => {
  if (jobs.length === 0) {
    return (
      <div className={styles.emptyState}>
        No ingestion jobs are currently in the queue. Start a crawl to populate this list.
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Program</th>
            <th>Stage</th>
            <th>Status</th>
            <th>Assigned</th>
            <th>Retries</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.job_id} onClick={() => onSelect(job.job_id)}>
              <td>
                <div className={styles.truncate} title={job.program_name ?? job.source_url}>
                  {job.program_name ?? job.source_url}
                </div>
                <div className={styles.summaryHint}>{job.source_url}</div>
              </td>
              <td><span className={styles.badge}>{toTitle(job.stage)}</span></td>
              <td>
                <span className={clsx(styles.statusChip, statusClassName(job.status))}>
                  {toTitle(job.status)}
                </span>
              </td>
              <td>{job.assigned_to_name ?? 'Unassigned'}</td>
              <td>{job.retry_count}</td>
              <td>{timeAgo(job.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface ActivityProps {
  entries: AwardIngestionStatusResponse['activity'];
}

const ActivityFeed: React.FC<ActivityProps> = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <div className={styles.emptyState}>
        No recent activity logged for award ingestion.
      </div>
    );
  }

  return (
    <div className={styles.activityList}>
      {entries.map((entry) => (
        <div key={entry.entry_id} className={styles.activityItem}>
          <span className={styles.activityTimestamp}>{formatDate(entry.occurred_at)}</span>
          <div className={styles.activityMessage}>
            <span>{entry.message}</span>
            <span className={styles.activityActor}>
              {entry.actor_name ? `By ${entry.actor_name}` : 'System generated'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

interface JobDrawerProps {
  jobId: string | null;
  onClose: () => void;
}

const JobDrawer: React.FC<JobDrawerProps> = ({ jobId, onClose }) => {
  const query = useAwardJobDetail(jobId);
  const recrawlMutation = useEnqueueAwardRecrawl();
  const assignMutation = useAssignAwardIngestionJob();

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const job = query.data?.job;

  const handleRecrawl = () => {
    if (!job) return;
    recrawlMutation.mutate(job.source_url);
  };

  const handleSelfAssign = () => {
    if (!job) return;
    assignMutation.mutate({ jobId: job.job_id, assigneeId: null });
  };

  if (!jobId) return null;

  return (
    <div className={styles.drawerBackdrop} onClick={handleBackdropClick}>
      <aside className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h3 className={styles.drawerTitle}>Job Detail</h3>
          <button type="button" className={styles.drawerCloseButton} onClick={onClose} aria-label="Close job detail">×</button>
          <div className={styles.summaryHint}>Job ID: {jobId}</div>
        </div>
        <div className={styles.drawerContent}>
          {query.isLoading ? (
            <div className={styles.loadingState}>Loading job detail…</div>
          ) : null}
          {query.isError ? (
            <div className={styles.errorState}>
              {awardIngestionErrorMessage(query.error)}
            </div>
          ) : null}
          {job ? (
            <>
              <div className={styles.drawerSection}>
                <span className={styles.drawerSectionTitle}>Program</span>
                <div className={styles.drawerList}>
                  <span>{job.program_name ?? 'Unknown program'}</span>
                  <a className={styles.linkButton} href={job.source_url} target="_blank" rel="noreferrer">
                    Open source
                  </a>
                </div>
              </div>

              <div className={styles.drawerSection}>
                <span className={styles.drawerSectionTitle}>Status Timeline</span>
                <div className={styles.drawerList}>
                  {query.data?.events.map((event) => (
                    <div key={`${event.status}-${event.occurred_at}`}>
                      <strong>{toTitle(event.status)}</strong>
                      {' • '}
                      {formatDate(event.occurred_at)}
                      {event.actor_name ? ` • ${event.actor_name}` : ''}
                      {event.note ? (
                        <div className={styles.summaryHint}>{event.note}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.drawerSection}>
                <span className={styles.drawerSectionTitle}>Artefacts</span>
                <div className={styles.drawerList}>
                  {query.data?.artefacts.length ? (
                    query.data?.artefacts.map((artefact) => (
                      <div key={`${artefact.artefact_type}-${artefact.created_at}`}>
                        <div><strong>{toTitle(artefact.artefact_type)}</strong> • {formatDate(artefact.created_at)}</div>
                        {artefact.preview ? (
                          <div className={styles.snippetBox}>{artefact.preview}</div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <span className={styles.summaryHint}>No artefacts captured yet.</span>
                  )}
                </div>
              </div>

              {query.data?.last_error ? (
                <div className={styles.drawerSection}>
                  <span className={styles.drawerSectionTitle}>Last error</span>
                  <div className={styles.snippetBox}>{query.data.last_error}</div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div className={styles.drawerActions}>
          <button
            type="button"
            className={styles.drawerSecondaryButton}
            onClick={handleSelfAssign}
            disabled={assignMutation.isPending || !job}
          >
            {assignMutation.isPending ? 'Assigning…' : 'Assign to me'}
          </button>
          <button
            type="button"
            className={styles.drawerPrimaryButton}
            onClick={handleRecrawl}
            disabled={recrawlMutation.isPending || !job}
          >
            {recrawlMutation.isPending ? 'Requeuing…' : 'Requeue crawl'}
          </button>
        </div>
        {(recrawlMutation.isError || assignMutation.isError) ? (
          <div className={styles.drawerActionFeedback}>
            {recrawlMutation.isError ? (
              <div>{awardIngestionErrorMessage(recrawlMutation.error)}</div>
            ) : null}
            {assignMutation.isError ? (
              <div>{awardIngestionErrorMessage(assignMutation.error)}</div>
            ) : null}
          </div>
        ) : null}
      </aside>
    </div>
  );
};

export const AwardIngestionView: React.FC = () => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const query = useAwardIngestionStatus();

  const summary = query.data?.summary ?? summaryDefaults;
  const activityEntries = query.data?.activity ?? [];

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <h2 className={styles.title}>Award Ingestion</h2>
          <p className={styles.subtitle}>
            Monitor crawl progress, extraction runs, and verification workload across all award program URLs.
          </p>
        </div>
        <div className={styles.headerMeta}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Last sync:</span>
            <span>{formatDate(query.data?.last_sync_at ?? null)}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Next crawl window:</span>
            <span>{formatDate(query.data?.next_crawl_window_at ?? null)}</span>
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <SummaryCards summary={summary} />

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableTitle}>Latest Jobs</h3>
          <div className={styles.tableActionGroup}>
            <button type="button" className={styles.secondaryButton} onClick={() => query.refetch()} disabled={query.isFetching}>
              Sync now
            </button>
          </div>
        </div>
        {query.isLoading ? (
          <div className={styles.loadingState}>Loading ingestion status…</div>
        ) : null}
        {query.isError ? (
          <div className={styles.errorState}>
            {awardIngestionErrorMessage(query.error)}
          </div>
        ) : null}
        {!query.isLoading && !query.isError ? (
          <JobTable jobs={query.data?.jobs ?? []} onSelect={setSelectedJobId} />
        ) : null}
      </section>

      <section>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableTitle}>Recent Activity</h3>
        </div>
        <ActivityFeed entries={activityEntries} />
      </section>

      <JobDrawer jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
    </section>
  );
};

export default AwardIngestionView;
